import { Outlet } from "react-router";
import type { ModuleConfig } from "@kubdee/shared";
import { SidebarProvider, SidebarInset } from "@kubdee/ui/shadcn/sidebar";
import { AppSidebar } from "@kubdee/ui/shell/app-sidebar";
import { ModuleGuard } from "@kubdee/ui/shell/module-guard";
import { resolveModuleNavigation } from "@/lib/resolve-module-navigation";

interface CreateModuleLayoutOptions {
  moduleId: string;
  moduleConfig: ModuleConfig;
  /**
   * Optional override for the sidebar title. When omitted (the default), the
   * title is auto-derived from the module manifest:
   *   `moduleConfig.navigation.label` → `moduleConfig.label`
   *
   * Prefer updating the manifest instead of passing this — the manifest label
   * is the single source of truth shared with the module switcher, permission
   * matrix, and marketplace listings. Use this override only for one-off brand
   * cases where the sidebar title must diverge from the manifest.
   */
  title?: string;
  /** Extra Tailwind classes appended to SidebarInset (default: "flex flex-col h-svh") */
  sidebarInsetClassName?: string;
}

/**
 * Factory that produces a module layout component.
 *
 * Eliminates boilerplate shared by every module layout — each file is reduced
 * to a single `createModuleLayout()` call + re-export.
 *
 * The returned component wraps child routes (via React Router `<Outlet />`)
 * with `ModuleGuard`, `SidebarProvider`, and `AppSidebar`.
 *
 * Also accepts explicit `children` for backward compatibility with
 * non-router usage.
 */
export function createModuleLayout({
  moduleId,
  title,
  moduleConfig,
  sidebarInsetClassName = "flex flex-col h-svh",
}: CreateModuleLayoutOptions) {
  const navGroups = resolveModuleNavigation(moduleConfig);
  // Prefer the navigation label (the user-facing sidebar text curated per
  // module) and fall back to the top-level manifest label. Both fields are
  // required on `ModuleConfig`, so a missing derived title means the manifest
  // itself is malformed — which typecheck will catch before we get here.
  const resolvedTitle = title ?? moduleConfig.navigation.label ?? moduleConfig.label;

  return function ModuleLayout({ children }: { children?: React.ReactNode }) {
    return (
      <ModuleGuard moduleId={moduleId}>
        <SidebarProvider>
          <AppSidebar moduleId={moduleId} title={resolvedTitle} navGroups={navGroups} />
          <SidebarInset className={sidebarInsetClassName || undefined}>
            {children ?? <Outlet />}
          </SidebarInset>
        </SidebarProvider>
      </ModuleGuard>
    );
  };
}
